import { ApRadio } from '@alixpartners/ui-components';
import { optionsToMainLabel } from 'components/UIComponents/GroupMultiSelect/GroupMultiSelect.utils';
import { IOption, MultiSelect } from 'components/UIComponents/MultiSelect';
import _ from 'lodash';
import {
  StructureFooter,
  StructureHeader,
  toStructureFooter,
  toStructureHeader,
  useExhibitExporterContext,
} from 'pages/ExhibitExporterPage/providers';
import { useMemo } from 'react';

const FOOTER_OPTIONS: IOption[] = [
  { label: 'Global footnotes', value: StructureFooter.Footnote },
  { label: 'Claim count', value: StructureFooter.ClaimCount },
];

const HEADER_OPTIONS: IOption[] = [
  { label: 'Hearing name', value: StructureHeader.Hearing },
  { label: 'Exhibit name', value: StructureHeader.Exhibit },
  { label: 'Basis for objection', value: StructureHeader.Objection },
  { label: 'Case number', value: StructureHeader.CaseNumber },
  { label: 'Case name', value: StructureHeader.CaseName },
];

export type OptionData = {
  createdBy: string;
  createdDate: string;
  id: number;
  name: string;
  updatedBy: string;
  updatedDate: string;
};

export const ExhibitStructureSection = () => {
  const { value, updateValue } = useExhibitExporterContext();
  const { structure } = value;
  const { header, footer, displayObjection } = structure;

  const selectedHeaderOptions = useMemo(
    () => HEADER_OPTIONS.filter((r) => header.includes(r.value as StructureHeader)),
    [header],
  );

  const selectedFooterOptions = useMemo(
    () => FOOTER_OPTIONS.filter((r) => footer.includes(r.value as StructureFooter)),
    [footer],
  );

  const handleHeaderChange = (e: IOption[]) => {
    const header = _(e)
      .map((r) => toStructureHeader(r.value))
      .compact()
      .value();

    const newStructure = { ...structure, header };
    updateValue('structure', newStructure);
  };

  const handleFooterChange = (e: IOption[]) => {
    const footer = _(e)
      .map((r) => toStructureFooter(r.value))
      .compact()
      .value();

    const newStructure = { ...structure, footer };
    updateValue('structure', newStructure);
  };

  const handleObjectionChange = (displayObjection: boolean) => () => {
    const newStructure = { ...structure, displayObjection };
    updateValue('structure', newStructure);
  };

  return (
    <div className="exhibit-exporter__section">
      <div className="exhibit-exporter__section-title">Exhibit Structure</div>
      <div className="exhibit-exporter__section-content-row">
        <div className="exhibit-exporter__section-content-row__multiselect-container">
          <label className="label">
            Information to be included in header of exhibit
          </label>
          <MultiSelect
            options={HEADER_OPTIONS}
            selected={selectedHeaderOptions}
            text={optionsToMainLabel(selectedHeaderOptions, 'Please select')}
            onChange={handleHeaderChange}
          />
        </div>
        <div className="exhibit-exporter__section-content-row__multiselect-container">
          <label className="label">
            Information to be included in footer of exhibit
          </label>
          <MultiSelect
            options={FOOTER_OPTIONS}
            selected={selectedFooterOptions}
            label={optionsToMainLabel(selectedFooterOptions, 'Please select')}
            onChange={handleFooterChange}
          />
        </div>

        <div className="exhibit-exporter__section-content-row__radio">
          <label className="label">
            Display objection commentary for each claim
          </label>
          <div className="exhibit-exporter__section-content-row__radio-container">
            <ApRadio
              autoFocus={false}
              checked={!!displayObjection}
              onChange={handleObjectionChange(true)}
              id="displayObjectionTrue"
              name="radio"
              theme="light"
              type="radio"
            >
              Yes
            </ApRadio>
            <ApRadio
              autoFocus={false}
              checked={!displayObjection}
              onChange={handleObjectionChange(false)}
              id="displayObjectionFalse"
              name="radio"
              theme="light"
              type="radio"
            >
              No
            </ApRadio>
          </div>
        </div>
      </div>
    </div>
  );
};
